import { ColumnType } from '../types';
import { TableViewState } from './types';
import {
  effectiveConfigurable, effectiveDefaultEnabled, isGroup, isSystem, stateKeyOf,
} from './columns';

/**
 * Whether a leaf column renders. System columns and effectively
 * non-`configurable` columns are always visible. With stored state, visibility
 * is membership in `state.visible`; without stored state, it is the effective
 * `defaultEnabled` (default true). Meta cascades from `parent` — group-level
 * `configurable`/`defaultEnabled` provide the default for children.
 */
export function isLeafVisible<R>(
  leaf: ColumnType<R>,
  state: TableViewState | null | undefined,
  parent?: ColumnType<R>,
): boolean {
  if (isSystem(leaf)) return true;
  if (!effectiveConfigurable(leaf, parent)) return true;
  if (state && Array.isArray(state.visible)) {
    return state.visible.includes(stateKeyOf(leaf));
  }
  return effectiveDefaultEnabled(leaf, parent);
}

/**
 * Filter columns to their visible set: hidden leaves are dropped, groups keep
 * only their visible children, and a group left with no visible children is
 * dropped entirely (preserving today's behavior). System columns — including
 * system groups — pass through untouched. A sub-group nested at depth ≥ 3 is an
 * atomic unit: it toggles by its own key and its inner structure is kept as
 * authored. Pure: touched groups are cloned, inputs are never mutated.
 */
export function applyVisibility<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): ColumnType<R>[] {
  const result: ColumnType<R>[] = [];
  for (const col of columns) {
    if (isSystem(col)) {
      result.push(col);
    } else if (isGroup(col)) {
      const children = col.children ?? [];
      const visibleChildren = children.filter((child) => isLeafVisible(child, state, col));
      if (visibleChildren.length === 0) continue;
      result.push(
        visibleChildren.length === children.length
          ? col
          : { ...col, children: visibleChildren },
      );
    } else if (isLeafVisible(col, state)) {
      result.push(col);
    }
  }
  return result;
}
